/**
 * Schema for profile cross-section preview
 * Renders the cross-section SVG path above the 3D case preview
 * WITHOUT coloring/filling logic
 */

import { measureSvgPath } from "./profilePreviewCell";

/** Target stroke thickness on page (pt); drawSvgPath width is in user space after scale — compensate */
const PROFILE_CROSS_SECTION_STROKE_PT = 1;

/**
 * Draw profile cross-section preview
 */
async function drawProfileCrossSection(arg: {
  pdfDoc: import("pdf-lib").PDFDocument;
  page: import("pdf-lib").PDFPage;
  schema: {
    position: { x: number; y: number };
    width: number;
    height: number;
  };
  value: string; // SVG path string for cross-section
}): Promise<void> {
  const { rgb } = await import("pdf-lib");
  const { value } = arg;

  if (!value || value.trim().length === 0) {
    return;
  }

  const { x, y } = arg.schema.position;
  const { width, height } = arg.schema;
  const pageHeight = arg.page.getHeight();

  // Convert mm to points
  const MM_TO_PT = 2.83465;
  const startX = x * MM_TO_PT;
  const startY = pageHeight - y * MM_TO_PT;

  // Measure the SVG path
  const bbox = measureSvgPath(value);
  if (bbox.width <= 0 || bbox.height <= 0) {
    return;
  }

  // Calculate available space in points
  const availableWidth = width * MM_TO_PT;
  const availableHeight = height * MM_TO_PT;

  // Calculate scale to fit in area (with padding)
  const padding = 4; // points
  const maxW = availableWidth - padding * 2;
  const maxH = availableHeight - padding * 2;

  const scaleW = maxW / bbox.width;
  const scaleH = maxH / bbox.height;
  const scale = Math.min(scaleW, scaleH);

  // Between left-aligned and centered: lerp 0.5 on path origin (≈25 % slack left vs. center)
  const centerX = startX + availableWidth / 2;
  const centerY = startY - availableHeight / 2;
  const pathXLeft = startX + padding - bbox.x * scale;
  const pathXCenter = centerX - (bbox.x + bbox.width / 2) * scale;
  const HORIZONTAL_BLEND = 0.5;
  const pathX = pathXLeft + HORIZONTAL_BLEND * (pathXCenter - pathXLeft);
  const pathY = centerY - (bbox.y + bbox.height / 2) * scale;

  // Line width scales with CTM; keep ~constant hairline on page (see pdf-lib drawSvgPath + scale)
  const borderWidth = Math.max(
    0.08,
    PROFILE_CROSS_SECTION_STROKE_PT / Math.max(scale, 1e-6),
  );

  // Draw SVG path as stroke only (no fill, no coloring logic)
  arg.page.drawSvgPath(value, {
    x: pathX,
    y: pathY,
    scale: scale,
    color: undefined, // No fill
    borderWidth,
    borderColor: rgb(0, 0, 0),
  });
}

export const profileCrossSectionSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.backgroundColor = "#f5f5f5";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.padding = "4px";
    div.style.boxSizing = "border-box";

    if (arg.value && arg.value.trim().length > 0) {
      const spacerBefore = document.createElement("div");
      spacerBefore.style.flex = "1";
      spacerBefore.style.minWidth = "0";

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("height", "100%");
      svg.style.width = "auto";
      svg.style.flexShrink = "0";
      svg.style.maxWidth = "100%";
      svg.style.maxHeight = "100%";

      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("d", arg.value);
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "#000");
      svg.appendChild(path);

      // Set viewBox based on path
      const bbox = measureSvgPath(arg.value);
      if (bbox.width > 0 && bbox.height > 0) {
        const padding = 2;
        svg.setAttribute(
          "viewBox",
          `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`,
        );
      }

      const spacerAfter = document.createElement("div");
      spacerAfter.style.flex = "3";
      spacerAfter.style.minWidth = "0";

      div.appendChild(spacerBefore);
      div.appendChild(svg);
      div.appendChild(spacerAfter);
    } else {
      div.textContent = "No cross-section";
      div.style.color = "#999";
      div.style.fontSize = "10px";
    }

    arg.rootElement.appendChild(div);
  },

  pdf: async (arg: {
    pdfDoc: import("pdf-lib").PDFDocument;
    page: import("pdf-lib").PDFPage;
    schema: {
      position: { x: number; y: number };
      width: number;
      height: number;
    };
    value: string;
  }) => {
    await drawProfileCrossSection(arg);
  },

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "profileCrossSection",
      type: "profileCrossSection",
      position: { x: 0, y: 0 },
      width: 90,
      height: 15,
    },
    defaultValue: "",
  },
};

export default profileCrossSectionSchema;
